import React from "react";
import { useDispatch, useSelector } from "react-redux";
import ReusableInput from "components/Inputs/ReusableInput";
import GradientButton from "components/Buttons/GradientButton";
import { Box, HStack, Text, Flex } from "@chakra-ui/react";
import { Check } from "lucide-react";
import {
  sendBroadcastNotificationAction,
  fetchAllNotificationsAction,
} from "state/actions/notificationActions";
import { useCustomNotification } from "components/Toasts/OpenNotification";

const userOptions = [
  { value: "Driver", label: "Drivers" },
  { value: "Individual", label: "Individuals" },
  { value: "Business", label: "Businesses" },
  { value: "Merchant", label: "Merchants" },
  { value: "Partner", label: "Partners" },
];

const notificationTypes = [
  "Customer Support",
  "Drivers",
  "Individuals",
  "Merchants",
  "Businesses",
  "General",
  "Security Alert",
];

const AddNotificationDrawer = ({ formData, setFormData, onClose }) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.notifications.loading);
  const notify = useCustomNotification();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUserToggle = (value) => {
    if (value === "ALL") {
      setFormData((prev) => ({
        ...prev,
        targetUsers: userOptions.map((u) => u.value),
      }));
      return;
    }

    setFormData((prev) => {
      const current = prev.targetUsers || [];
      return {
        ...prev,
        targetUsers: current.includes(value)
          ? current.filter((u) => u !== value)
          : [...current, value],
      };
    });
  };

  const handleSave = async () => {
    if (
      !formData.message ||
      !formData.notificationType ||
      !formData.targetUsers?.length
    ) {
      notify({
        message: "Missing fields",
        description: "Please fill all required fields",
        type: "warning",
      });
      return;
    }

    const payload = {
      user_type: formData.targetUsers,
      message: formData.message,
      notification_type: formData.notificationType,
    };

    const result = await dispatch(sendBroadcastNotificationAction(payload));

    if (result.success) {
      dispatch(fetchAllNotificationsAction());

      notify({
        message: "Broadcast sent",
        description: "Notification sent successfully",
        type: "success",
      });

      onClose();
    } else {
      notify({
        message: "Error",
        description: result.error || "Failed to send notification",
        type: "error",
      });
    }
  };

  const isAllSelected =
    formData.targetUsers?.length === userOptions.length;

  return (
    <Box>
      {/* TARGET USERS */}
      <Box mb={4}>
        <Text fontSize="sm" color="#8D93A1" fontWeight="medium" mb={3}>
          Target Users
        </Text>

        <Flex gap={3} wrap="wrap">
          {/* ALL */}
          <Flex
            px={4}
            py={2}
            borderRadius="12px"
            cursor="pointer"
            fontSize="sm"
            fontWeight="500"
            align="center"
            gap={1}
            border="1px solid"
            borderColor={isAllSelected ? "#071D54" : "gray.300"}
            bg={isAllSelected ? "#071D54" : "transparent"}
            color={isAllSelected ? "white" : "gray.600"}
            onClick={() => handleUserToggle("ALL")}
          >
            {isAllSelected && <Check size={14} />}
            All
          </Flex>

          {/* USERS */}
          {userOptions.map((user) => {
            const isSelected = formData.targetUsers?.includes(user.value);

            return (
              <Flex
                key={user.value}
                px={4}
                py={2}
                borderRadius="12px"
                cursor="pointer"
                fontSize="sm"
                fontWeight="500"
                align="center"
                gap={1}
                border="1px solid"
                borderColor={isSelected ? "#071D54" : "gray.300"}
                bg={isSelected ? "#071D54" : "transparent"}
                color={isSelected ? "white" : "gray.600"}
                onClick={() => handleUserToggle(user.value)}
                transition="all 0.2s ease"
              >
                {isSelected && <Check size={14} />}
                {user.label}
              </Flex>
            );
          })}
        </Flex>
      </Box>

      {/* TYPE */}
      <ReusableInput
        label="Notification Type"
        name="notificationType"
        type="select"
        value={formData.notificationType}
        onChange={handleInputChange}
        options={notificationTypes.map((t) => ({
          value: t,
          label: t,
        }))}
      />

      {/* MESSAGE */}
      <ReusableInput
        label="Message"
        name="message"
        value={formData.message}
        onChange={handleInputChange}
        placeholder="Enter message"
        type="textarea"
      />

      {/* ACTIONS */}
      <HStack justifyContent="flex-end" mt={4}>
        <GradientButton onClick={onClose} disabled={loading} height="35px" fontSize="14px">
          Close
        </GradientButton>
        <GradientButton onClick={handleSave} loading={loading} height="35px" fontSize="14px">
          Send
        </GradientButton>
      </HStack>
    </Box>
  );
};

export default AddNotificationDrawer;